import { nextTick } from "vue";
import { iframeMutations } from "@/shared/store/iframe.mutations";
import { copy, parseHtml } from "@/shared/utils/string";
import { waitForMe, cloneDeep } from "@/shared/utils/helpers";
import { tickersService } from "../services/tickers.service";
import { projectsService } from "@/shared/projects/projects.service";
import { prepareTicker } from "@/shared/projects/prepareData";
import { tickerSnapshotService } from "@/apps/ticker/store/ticker-snapshot.service";
import { tickerCartService } from "@/apps/ticker/services/ticker-cart.service";
import { tickerUploadsService } from "../services/ticker-uploads.service";
import { makeLogger } from "@/shared/consola";
import { t } from "@/i18n";

const logger = makeLogger("ticker.store");

export default {
    namespaced: true,
    state: () => ({
        ticker: {},
        events: [],
        colorsGhost: {}, // Not needed for state (just for snapshot logic)
        iframe: null,
        iframeLoaded: false,
        hasChanges: false,
        hasSnapshot: false,
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.ticker,
    },
    mutations: {
        ...iframeMutations,
        setProject(state, { ticker, events, snapshot = {} }) {
            logger.log("Making snapshot with: ", ticker);
            state.ticker = tickerSnapshotService.makeSnapshotState(
                state,
                "ticker",
                ticker,
                snapshot
            );
            state.events = tickerSnapshotService.makeSnapshotState(
                state,
                "events",
                events,
                snapshot
            );
            state.ticker.colors = tickerSnapshotService.makeSnapshotState(
                state,
                "colorsGhost",
                state.ticker.colors || {},
                snapshot
            );
            state.hasChanges = false;
        },
        clearProject(state) {
            state.ticker = {};
            state.events = [];
            state.eventsGhost = {};
            state.colorsGhost = {};
            tickerSnapshotService.clearSnapshot(state, "ticker", {});
            tickerSnapshotService.clearSnapshot(state, "events", []);
            tickerSnapshotService.clearSnapshot(state, "colorsGhost", {});
        },
        addEcProduct(state, ecId) {
            state.ticker.ecProductIds.push({ id: ecId });
        },
        removeEcProduct(state, ecId) {
            const index = state.ticker.ecProductIds.findIndex((product) => product.id === ecId);
            if (index !== -1) {
                state.ticker.ecProductIds.splice(index, 1);
            }
        },
        addEvent(state, code) {
            state.events.push({
                _created: true,
                code: code,
                active: true,
                tickerId: state.ticker.id,
            });
        },
        changeEvent(state, payload) {
            const updatedEvent = Object.assign({}, payload);
            const index = state.events.findIndex((e) => e.code === payload.code);
            if (index !== -1) {
                state.events.splice(index, 1, updatedEvent);
            }
        },
    },
    actions: {
        refreshIframe({ state, getters }) {
            const ticker = cloneDeep(state.ticker);
            ticker.events = cloneDeep(state.events);
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && ticker.id) {
                state.iframe.contentWindow.postMessage({ tickerData: ticker }, getters.appUrl);
            }
        },
        async getECProducts({ rootState, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            rootState.waiting = true;
            //TODO: move to shared
            const data = await tickerCartService.getECProducts();
            rootState.waiting = false;
            return data;
        },
        async connectWithECProduct({ commit, dispatch }, { id, add }) {
            if (add) {
                commit("addEcProduct", id);
            } else {
                commit("removeEcProduct", id);
            }

            dispatch("mergeSnapshotChanges");
        },
        async connectWithECProducts({ commit, dispatch }, { ids, add }) {
            ids.forEach((id) => {
                if (add) {
                    commit("addEcProduct", id);
                } else {
                    commit("removeEcProduct", id);
                }
            });

            dispatch("mergeSnapshotChanges");
        },
        async saveSnapshot({ rootState, state, dispatch }) {
            rootState.waiting = true;
            try {
                await tickerSnapshotService.updateSnapshotAfterSave(state.ticker.uuid, {
                    stateSlice: state,
                    actionToTakeIfNotRequired: () => dispatch("mergeSnapshotChanges"),
                });
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async mergeSnapshotChanges({ state, rootState, commit }) {
            await nextTick();

            rootState.waiting = true;

            const ticker = cloneDeep(state.ticker);
            ticker.events = state.events;

            try {
                const project = await projectsService.update({
                    tool: "ticker",
                    uuid: state.ticker.uuid,
                    data: ticker,
                });

                state.hasSnapshot = false;

                const updatedTicker = prepareTicker(project);

                commit("showNotify", "autoSave", { root: true });
                commit("setProject", {
                    ticker: updatedTicker,
                    events: updatedTicker.events,
                    snapshot: {},
                });
            } catch {
                /* empty */
            } finally {
                rootState.waiting = false;
            }
        },
        async loadProject({ rootState, commit }, uuid) {
            rootState.waiting = true;
            try {
                const { project, snapshot } = await projectsService.getOneWithSnapshot({
                    tool: "ticker",
                    uuid,
                });
                const ticker = prepareTicker(project);
                commit("setProject", { ticker, events: ticker.events, snapshot });
            } catch {
                /* empty */
            } finally {
                rootState.waiting = false;
            }
        },
        generateScript({ state, rootState }) {
            const functionModule = require("@/apps/ticker/config/ticker.js");
            let functionText = functionModule.toString();

            const { uuid, showIcon } = state.ticker;
            functionText = functionText.replace("uuidToReplace", uuid);
            functionText = functionText.replace("originToReplace", rootState.globalUrl);
            functionText = functionText.replace("langToReplace", rootState.previewLang);
            functionText = functionText.replace("showIconToReplace", showIcon ? "1" : "");

            functionText = `(${functionText})();`;
            return functionText;
        },
        publishProject({ rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            dispatch("hostCode");
        },
        async unpublishTicker({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.ticker.uuid });
            commit("setUnpublished", state.ticker, { root: true });
            dispatch("mergeSnapshotChanges");
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        async unpublish({ _ }, { uuid }) {
            return tickersService.publish({ uuid, code: "", publish: false });
        },
        showCodeModal({ state, rootState, commit, dispatch }) {
            const scriptLink = `<script src="${rootState.globalUrl}/tickers/scripts/${state.ticker.uuid}.js" type="text/javascript"></script>`;
            const modalData = {
                type: "code",
                title: t("ticker.code.title"),
                desc: t("ticker.code.desc"),
                code: scriptLink,
                actionBtn: t("shared.copy_code"),
                cancelBtn: t("shared.close"),
                callback: () => {
                    copy(scriptLink);
                    dispatch(
                        "alert/showSuccessMessage",
                        { title: t("shared.code_copied") },
                        { root: true }
                    );
                },
            };
            commit("showModal", modalData, { root: true });
        },
        async hostCode({ state, rootState, rootGetters, getters, commit, dispatch }) {
            rootState.waiting = true;

            state.iframe.contentWindow.postMessage({ showPreview: "off" }, getters.appUrl);

            await waitForMe(100);
            await nextTick();

            const firstPublish = !rootGetters.isPublished(state.ticker);

            const scriptText = await dispatch("generateScript");

            try {
                await tickersService.publish({
                    uuid: state.ticker.uuid,
                    code: parseHtml(scriptText),
                    publish: true,
                });

                state.ticker.code = parseHtml(scriptText);
                commit("setPublished", state.ticker, { root: true });
                dispatch("mergeSnapshotChanges");
                if (firstPublish) {
                    dispatch("showCodeModal");
                }
                rootState.waiting = false;
            } catch {
                rootState.waiting = false;
                return false;
            } finally {
                state.iframe.contentWindow.postMessage({ showPreview: "on" }, getters.appUrl);
            }
        },
        changeFontFamily({ state }, payload) {
            state.ticker.fontFamily = payload;
        },
        async readImageFile({ state, rootState }, payload) {
            rootState.waiting = true;
            try {
                const { image } = await tickerUploadsService.tickerImage({
                    file: payload.file,
                    tickerId: state.ticker.id,
                });
                rootState.waiting = false;
                return image;
            } catch {
                /* eslint-disable-next-line no-empty */
                return "";
            }
        },
    },
};
